import type { CommercialSummary, CommercialSummaryInput } from '@solar/contracts';

function toCents(value: string): number {
  const parsed = Number.parseFloat(value);

  if (Number.isNaN(parsed)) {
    throw new Error(`Invalid money value: ${value}`);
  }

  return Math.round(parsed * 100);
}

function fromCents(cents: number): string {
  return (cents / 100).toFixed(2);
}

/** Client-side preview of commercial terms, mirroring the API calculator. */
export function calculateCommercialSummary(input: CommercialSummaryInput): CommercialSummary {
  const productPriceCents = toCents(input.productPrice);
  const discountCents = toCents(input.discountAmount ?? '0.00');

  if (discountCents > productPriceCents) {
    throw new Error('Discount cannot exceed the product price.');
  }

  const netPriceCents = productPriceCents - discountCents;

  if (netPriceCents <= 0) {
    throw new Error('Net product price must be positive.');
  }

  let depositCents: number;

  if (input.depositType === 'FIXED') {
    depositCents = toCents(input.depositValue);

    if (depositCents >= netPriceCents) {
      throw new Error('Deposit must be less than the net product price.');
    }
  } else {
    const percentage = Number.parseFloat(input.depositValue);

    if (Number.isNaN(percentage) || percentage < 0 || percentage > 100) {
      throw new Error('Deposit percentage must be between 0 and 100.');
    }

    depositCents = Math.round((netPriceCents * percentage) / 100);
  }

  if (depositCents >= netPriceCents) {
    throw new Error('Deposit must be less than the net product price.');
  }

  if (!Number.isInteger(input.tenureMonths) || input.tenureMonths <= 0) {
    throw new Error('Tenure must be a positive whole number of months.');
  }

  const financedCents = netPriceCents - depositCents;
  const tenureMonths = input.tenureMonths;
  const baseInstalmentCents = Math.floor(financedCents / tenureMonths);
  const regularTotalCents = baseInstalmentCents * (tenureMonths - 1);
  const finalInstalmentCents = financedCents - regularTotalCents;
  const totalRepayableCents = depositCents + financedCents;

  return {
    productPrice: fromCents(productPriceCents),
    discountAmount: fromCents(discountCents),
    depositAmount: fromCents(depositCents),
    financedAmount: fromCents(financedCents),
    totalRepayableAmount: fromCents(totalRepayableCents),
    tenureMonths,
    instalmentAmount: fromCents(baseInstalmentCents),
    finalInstalmentAmount: fromCents(finalInstalmentCents),
  };
}
